import { Link } from "react-router-dom";
import { Product } from "@/mocks/products";
import { useCart } from "@/hooks/useCart";
import { useState } from "react";

interface ProductCardProps {
  product: Product;
}

const StarRating: React.FC<{ rating: number; size?: string }> = ({ rating, size = "text-xs" }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <i
        key={star}
        className={`${size} ${
          star <= Math.floor(rating)
            ? "ri-star-fill text-amber-400"
            : star - 0.5 <= rating
            ? "ri-star-half-fill text-amber-400"
            : "ri-star-line text-gray-300"
        }`}
      ></i>
    ))}
  </div>
);

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);
  const discount = Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart({
      productId: product.id,
      quantity: 1,
      price: product.price,
      name: product.name,
      image: product.images[0],
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <Link
      to={`/product/${product.id}`}
      className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-orange-200 transition-all duration-300 hover:-translate-y-1 flex flex-col cursor-pointer"
      data-product-shop
    >
      {/* Image */}
      <div className="relative overflow-hidden bg-gray-50 aspect-square">
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {product.badge && (
          <span className="absolute top-3 left-3 px-2 py-1 bg-orange-500 text-white text-xs font-semibold rounded-md">
            {product.badge}
          </span>
        )}
        {discount > 0 && (
          <span className="absolute top-3 right-3 px-2 py-1 bg-red-500 text-white text-xs font-semibold rounded-md">
            -{discount}%
          </span>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300"></div>
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        <p className="text-xs text-orange-500 font-medium uppercase tracking-wide mb-1">
          {product.category.replace("-", " ")}
        </p>
        <h3 className="text-sm font-semibold text-gray-800 leading-snug mb-2 line-clamp-2 flex-1">
          {product.name}
        </h3>

        <div className="flex items-center gap-2 mb-3">
          <StarRating rating={product.rating} />
          <span className="text-xs text-gray-500">({product.reviewCount})</span>
        </div>

        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-lg font-bold text-gray-900">${product.price}</span>
            {product.originalPrice > product.price && (
              <span className="text-xs text-gray-400 line-through ml-2">${product.originalPrice}</span>
            )}
          </div>
        </div>

        <button
          onClick={handleAddToCart}
          className={`w-full py-2 text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer ${
            added
              ? "bg-green-500 text-white"
              : "bg-orange-500 text-white hover:bg-orange-600"
          }`}
        >
          {added ? (
            <><i className="ri-check-line mr-1"></i>Added!</>
          ) : (
            <><i className="ri-shopping-cart-line mr-1"></i>Add to Cart</>
          )}
        </button>
      </div>
    </Link>
  );
};

export { StarRating };
export default ProductCard;
